import "./SpasPage.css"
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppDispatch } from "../../redux/store";
import {fetchSpaCount, fetchSpas} from "../../redux/actions/spas.actions";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import FirmwareUpdateModal from "../../components/common/Modals/FirmwareUpdateModal";
import RequestLogsModal from "../../components/common/Modals/RequestLogsModal";
import SpasContainer from "./components/SpasContainer";
import DeleteSpaModal from "../../components/common/Modals/DeleteSpaModal";
import EditSpaModal from "../../components/common/Modals/EditSpaModal";
import ConfirmFirmwareModal from "../../components/common/Modals/ConfirmFirmwareModal";
import { V1FirmwareChannel } from "../../services/api/v1Firmware.api";
import ConfirmLogsModal from "../../components/common/Modals/ConfirmLogsModal";
import AddSpaCommandsModal from "./components/AddSpaCommandsModal";
import { fetchUserCount } from "../../redux/actions/users.actions";
import usePermissionCheck from "../../hooks/usePermissionCheck";
import useRoleCheck from "../../hooks/useRoleCheck";
import { PersonIcon } from "../../components/common/Icons/Icons";

// These roles start each visit on registered spas; unowned spas from every dealership are one click away
const REGISTERED_BY_DEFAULT_ROLES = ['dealer', 'owner', 'corporate'];

const SpasPage: React.FC = () => {
  // Modals
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [confirmLogs, setConfirmLogs] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addCommandsOpen, setAddCommandsOpen] = useState(false);

  const [spaToModify, setSpaToModify] = useState<any>(null);

  // Data
  const dispatch: AppDispatch = useDispatch();
  const {data: currentUser} = useSelector((state: RootState) => state.user);
  const {online, total} = useSelector((state: RootState) => state.spaCount);
  const { count: usersCount } = useSelector((state: RootState) => state.users);
  const [searchParams, setSearchParams] = useSearchParams();
  const {canAccess: canAddCommands} = usePermissionCheck(['assign_spa_api_commands']);
  const {canAccess: registeredByDefault, checking: checkingRole} = useRoleCheck(REGISTERED_BY_DEFAULT_ROLES);
  const defaultFilterApplied = useRef(false);

  useEffect(() => {
    if (checkingRole) return;

    // Once per visit, so choosing "All" sticks until the page is left and reopened
    if (!defaultFilterApplied.current) {
      defaultFilterApplied.current = true;
      if (registeredByDefault && !searchParams.has('registered')) {
        searchParams.set('registered', 'true');
        setSearchParams(searchParams, {replace: true});
        return;
      }
    }

    dispatch(fetchSpas(searchParams.toString()));

    if (usersCount === 0) {
      dispatch(fetchUserCount());
    }

    if (total === 0) {
      dispatch(fetchSpaCount());
    }

  }, [dispatch, searchParams, checkingRole]);

  // Listen for dealership impersonation changes and refetch data
  useEffect(() => {
    const handleDealershipChange = () => {
      dispatch(fetchSpas(searchParams.toString()));
      dispatch(fetchSpaCount());
      dispatch(fetchUserCount());
    };

    window.addEventListener('impersonate_dealership_change', handleDealershipChange);

    return () => {
      window.removeEventListener('impersonate_dealership_change', handleDealershipChange);
    };
  }, [dispatch, searchParams]);

  // User Input
  const [firmware, setFirmware] = useState("");
  const [version, setVersion] = useState("");
  const [v1Channel, setV1Channel] = useState<V1FirmwareChannel>('public');

  // Checked items
  const [checked, setChecked] = useState<any[]>([]);
  
  
  const getOnlineSelectedSpas = () => {
    return [...checked].filter((spa:any)=>spa?.is_connected);
  }

  return (
    <div className="page lp-page">
      {confirmUpdate &&<ConfirmFirmwareModal 
        isOpen={confirmUpdate} 
        setOpen={setConfirmUpdate} 
        setOpenMultiple={setUpdateOpen}
        setFirmware={setFirmware}
        setVersion={setVersion}
        setChannel={setV1Channel}
        spas={getOnlineSelectedSpas()}
      />
      }

      {confirmLogs && <ConfirmLogsModal
        isOpen={confirmLogs} 
        setOpen={setConfirmLogs}
        setOpenRequest={setRequestOpen}
        spas={getOnlineSelectedSpas()}
      />
      }

      {updateOpen && <FirmwareUpdateModal 
        isOpen={updateOpen} 
        setOpen={setUpdateOpen} 
        spas={getOnlineSelectedSpas()} 
        firmwareType={firmware} 
        version={version}
        channel={v1Channel}
      />}

      {requestOpen && <RequestLogsModal 
        isOpen={requestOpen} 
        setOpen={setRequestOpen} 
        spas={getOnlineSelectedSpas()}
      />}
      
      {deleteOpen && <DeleteSpaModal 
        open={deleteOpen} 
        setOpen={setDeleteOpen} 
        object={spaToModify} 
      />}

      {editOpen && <EditSpaModal 
        open={editOpen} 
        setOpen={setEditOpen} 
        object_id={spaToModify?.id}
      />}

      {addCommandsOpen && canAddCommands && <AddSpaCommandsModal 
        open={addCommandsOpen} 
        setOpen={setAddCommandsOpen} 
        selection={checked}  
      />}
      
      <Header
        header="Spas"
        headerExtra={
          <ul className="lp-stats" aria-label="Summary">
            <li className="lp-stat"><PersonIcon/><b>{(usersCount??0).toLocaleString()}</b> Users</li>
            <li className="lp-stat online"><span className="lp-dot" aria-hidden="true"/><b>{(online??0).toLocaleString()}</b> Online</li>
            <li className="lp-stat"><span className="lp-dot" aria-hidden="true"/><b>{((total??0)-(online??0)).toLocaleString()}</b> Offline</li>
          </ul>
        }
      />
      <main className="lp-main">
        <SpasContainer
          checked={checked}
          setChecked={setChecked}
          setConfirmUpdate={setConfirmUpdate}
          setConfirmLogs={setConfirmLogs}
          setDeleteOpen={setDeleteOpen}
          setEditOpen={setEditOpen}
          setAddCommandsOpen={setAddCommandsOpen}
          setSpaToModify={setSpaToModify}
        />
      </main>
    </div>
  );
};

export default SpasPage;
